import { createRouter, createWebHashHistory, type RouteRecordRaw } from "vue-router";
import { tokenStorage, authEvents } from "@/api/client";
import { useAuth } from "@/composables/useAuth";
import { hydrateSharedTokens, isAllowedReturnUrl } from "@/utils/authSession";

import IELTS from "./views/IELTS.vue";
import MockExams from "./views/MockExams.vue";
import ReadingPractice from "./views/ReadingPractice.vue";
import writingExercises from "./views/WritingExercises.vue";
import OralPractice from "./views/OralPractice.vue";
import PracticeRecords from "./views/PracticeRecords.vue";
import MockExamPageDetail from "@/components/pages/MockExamPageDtail.vue";
import ListeningExamPage from "@/views/ListeningExamPage.vue";
import Login from "@/views/Login.vue";
import SpeakingView from "@/views/SpeakingView.vue";
import WritingView from "@/views/WritingView.vue";
import VocabularyLibrary from "@/views/VocabularyLibrary.vue";
import LeitnerBox from "@/views/LeitnerBox.vue";
import LeitnerReview from "@/views/LeitnerReview.vue";
import CommunityTalk from "@/views/CommunityTalk.vue";
import SignUp from "@/views/SignUp.vue";
import ForgotPassword from "@/views/ForgotPassword.vue";
import Profile from "@/views/Profile.vue";
import Settings from "@/views/Settings.vue";

const routes: RouteRecordRaw[] = [
  {
    path: "/",
    name: "login",
    component: Login,
    meta: { hideHeaderFooter: true, public: true },
  },
  {
    path: "/login",
    redirect: "/",
  },
  {
    path: "/signup",
    name: "signup",
    component: SignUp,
    meta: { hideHeaderFooter: true, public: true },
  },
  {
    path: "/forgot-password",
    name: "forgot-password",
    component: ForgotPassword,
    meta: { hideHeaderFooter: true, public: true },
  },
  {
    path: "/dashboard",
    name: "dashboard",
    component: IELTS,
    meta: {
      hideHeaderFooter: true,
    },
  },
  {
    path: "/profile",
    name: "profile",
    component: Profile,
    meta: { hideHeaderFooter: true },
  },
  {
    path: "/settings",
    name: "settings",
    component: Settings,
    meta: { hideHeaderFooter: true },
  },
  {
    path: "/ielts",
    redirect: "/dashboard",
  },
  {
    path: "/mock-exams",
    component: MockExams,
    meta: {
      hideHeaderFooter: true,
    },
  },
  {
    path: "/reading-practice",
    component: ReadingPractice,
  },
  {
    path: "/writing-exercises",
    component: writingExercises,
  },
  {
    path: "/oral-practice",
    component: OralPractice,
  },
  {
    path: "/practice-records",
    component: PracticeRecords,
  },
  {
    path: "/mock-exam/:level/:testId",
    name: "mockExamPageDtail",
    component: MockExamPageDetail,
    meta: {
      hideHeaderFooter: true,
    },
  },
  {
    path: "/listening-exam/:level/:testId",
    name: "listeningExamPage",
    component: ListeningExamPage,
    meta: {
      hideHeaderFooter: true,
    },
    children: [
      {
        path: "",
        component: () => import("@/components/parts/Part1.vue"),
      },
      {
        path: "part-2",
        component: () => import("@/components/parts/Part2.vue"),
      },
      {
        path: "part-3",
        component: () => import("@/components/parts/Part3.vue"),
      },
      {
        path: "part-4",
        component: () => import("@/components/parts/Part4.vue"),
      },
    ],
  },
  {
    path: "/reading-exam/:level/:testId",
    name: "readingExamPage",
    component: () => import("@/views/ReadingExamPage.vue"),
    meta: {
      hideHeaderFooter: true,
    },
    children: [
      {
        path: "",
        component: () => import("@/components/readingParts/Part1.vue"),
      },
      {
        path: "part-2",
        component: () => import("@/components/readingParts/Part2.vue"),
      },
      {
        path: "part-3",
        component: () => import("@/components/readingParts/Part3.vue"),
      },
    ],
  },
  {
    path: "/writing-exam/:level/:testId",
    name: "writingExamPage",
    component: () => import("@/views/WritingExamPage.vue"),
    meta: {
      hideHeaderFooter: true,
    },
    children: [
      {
        path: "",
        component: () => import("@/components/writingParts/Part1.vue"),
      },
      {
        path: "part-2",
        component: () => import("@/components/writingParts/Part2.vue"),
      },
    ],
  },
  {
    path: "/dashboard/speaking",
    name: "dashboard-speaking",
    component: SpeakingView,
    meta: {
      hideHeaderFooter: true,
    },
  },
  {
    path: "/dashboard/writing",
    name: "dashboard-writing",
    component: WritingView,
    meta: {
      hideHeaderFooter: true,
    },
  },
  {
    path: "/dashboard/vocabulary",
    name: "dashboard-vocabulary",
    component: VocabularyLibrary,
    meta: {
      hideHeaderFooter: true,
    },
  },
  {
    path: "/dashboard/leitner",
    name: "dashboard-leitner",
    component: LeitnerBox,
    meta: {
      hideHeaderFooter: true,
    },
  },
  {
    path: "/dashboard/leitner/review",
    name: "dashboard-leitner-review",
    component: LeitnerReview,
    meta: {
      hideHeaderFooter: true,
    },
  },
  {
    path: "/dashboard/talk",
    name: "dashboard-talk",
    component: CommunityTalk,
    meta: {
      hideHeaderFooter: true,
    },
  },
  { path: "/ielts/speaking", redirect: "/dashboard/speaking" },
  { path: "/ielts/writing", redirect: "/dashboard/writing" },
  { path: "/ielts/vocabulary", redirect: "/dashboard/vocabulary" },
  { path: "/ielts/leitner", redirect: "/dashboard/leitner" },
  { path: "/ielts/talk", redirect: "/dashboard/talk" },
];

const router = createRouter({
  history: createWebHashHistory(),
  routes,
  linkActiveClass: "active",
  linkExactActiveClass: "active",
});

let authInitialized = false;

router.beforeEach(async (to, from, next) => {
  hydrateSharedTokens();

  if (!authInitialized) {
    const { init } = useAuth();
    await init();
    authInitialized = true;
  }

  const isPublic = to.meta.public === true;
  const hasToken = !!tokenStorage.getAccess();
  const redirectQuery =
    typeof to.query.redirect === "string" ? to.query.redirect : "";

  if (!hasToken && !isPublic) {
    next({
      name: "login",
      query: to.fullPath !== "/" ? { redirect: to.fullPath } : {},
    });
    return;
  }

  if (hasToken && isPublic) {
    if (redirectQuery && isAllowedReturnUrl(redirectQuery)) {
      window.location.assign(redirectQuery);
      return;
    }
    next({ path: "/dashboard" });
    return;
  }

  next();
});

authEvents.addEventListener("unauthorized", () => {
  if (router.currentRoute.value.name !== "login") {
    router.push({ name: "login" });
  }
});

export default router;
